import { useSlideOverContext } from '@/hooks/useSlideOverContext';
import { Button } from '@/lib';
import { SpinnerIcon } from '@/lib/Spinner';
import { DEFAULT_PAGE_SIZE } from '@/lib/Tables/parts/TablePagination/constants';
import { TablePagination } from '@/lib/Tables/parts/TablePagination/TablePagination';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useCompanyValues } from '@/providers';
import { useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { WhatsappTemplate } from '@/features/campaigns/v2/types/whatsappTemplates';
import { SmsTemplate } from '@/features/campaigns/v2/Sms/Templates/smsTemplateListFlowMapper';
import { useEmailTemplates } from '@/features/campaigns/EmailCampaign/hooks/useEmailTemplates';
import { SMS_CONNECTOR_NAME } from '@/features/connectors/sms';
import { SmsWorkspaceProvider, useSmsWorkspaceTemplates } from '@/features/campaigns/v2/Sms/context/SmsWorkspaceContext';
import { ActionButton } from '@/features/campaigns/v2/Whatsapp/ActionButton';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemModel } from '../../templates/catalog';
import { buildWorkspaceTemplateRows } from '../../templates/buildWorkspaceTemplateRows';
import { SystemModelStatus } from '../../templates/modelStatus';
import {
  partitionWorkspaceTemplates,
  type WorkspaceTemplateRow,
} from '../../templates/partitionWorkspaceTemplates';
import { ChannelIndicator } from './ChannelIndicator';
import { ModelliTabContent } from './ModelliTabContent';
import { ModelloPreviewSlideOver } from './ModelloPreviewSlideOver';
import { stripTopicPrefix } from '../../templates/topicPrefix';

type MyModelsListProps = {
  rows: WorkspaceTemplateRow[];
  settling: boolean;
  waTemplatesError?: boolean;
  onRetryWa?: () => void;
  onPreview: (row: WorkspaceTemplateRow) => void;
  onEdit: (row: WorkspaceTemplateRow) => void;
  onDelete: (row: WorkspaceTemplateRow) => void;
};

const MyModelsList: FC<MyModelsListProps> = ({ rows, settling, waTemplatesError, onRetryWa, onPreview, onEdit, onDelete }) => {
  const { t } = useTranslation('comuni');
  const [pageNo, setPageNo] = useState(0);

  useEffect(() => {
    setPageNo(0);
  }, [rows]);

  const pageRows = useMemo(() => rows.slice(pageNo * DEFAULT_PAGE_SIZE, (pageNo + 1) * DEFAULT_PAGE_SIZE), [pageNo, rows]);

  return (
    <section className='grid w-full grid-cols-1 gap-3'>
      <div>
        <h3 className='text-lg font-medium'>{t('modelli.myModels.title')}</h3>
        <p className='mt-0.5 text-sm text-tertiary-600'>{t('modelli.myModels.subtitle')}</p>
      </div>

      {settling && rows.length === 0 ? (
        <div className='flex justify-center py-10' role='status' aria-busy='true'>
          <SpinnerIcon className='size-8 text-brand-700' />
        </div>
      ) : rows.length === 0 && waTemplatesError ? (
        <div className='flex flex-col items-center gap-3 rounded-xl border border-gray-200 px-4 py-8 text-center'>
          <p className='text-sm text-tertiary-600'>{t('modelli.myModels.loadError')}</p>
          {onRetryWa ? <Button intent='secondary' size='md' text={t('modelli.myModels.retry')} onClick={onRetryWa} /> : null}
        </div>
      ) : rows.length === 0 ? (
        <p className='rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-tertiary-600'>{t('modelli.myModels.empty')}</p>
      ) : (
        <div className='w-full grid grid-cols-1 gap-3'>
          <ul className='divide-y divide-gray-100 rounded-xl border border-gray-200'>
            {pageRows.map((row) => (
              <li key={row.id} className='flex items-center gap-4 px-4 py-3'>
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-2'>
                    <span className='font-semibold '>{stripTopicPrefix(row.name)}</span>
                    <ChannelIndicator channel={row.channel} />
                  </div>
                  <p className='mt-0.5 truncate text-sm text-tertiary-600'>{row.detail}</p>
                </div>
                <div className='flex flex-none items-center gap-3'>
                  <button type='button' className='text-sm font-semibold text-brand-700 hover:text-brand-800' onClick={() => onPreview(row)}>
                    {t('modelli.preview')}
                  </button>
                  <ActionButton onEdit={() => onEdit(row)} onDelete={() => onDelete(row)} />
                </div>
              </li>
            ))}
          </ul>
          <TablePagination limit={DEFAULT_PAGE_SIZE} currentPage={pageNo + 1} totalAmount={rows.length} onPageClick={setPageNo} />
        </div>
      )}
    </section>
  );
};

type ListsBodyProps = {
  waTemplates: WhatsappTemplate[];
  smsTemplates: SmsTemplate[];
  connectorsLoading: boolean;
  smsLoading?: boolean;
  waTemplatesLoading?: boolean;
  waTemplatesError?: boolean;
  onRetryWa?: () => void;
  statusById: Map<string, SystemModelStatus>;
  statusSkeleton: boolean;
  onPreviewSystem: (model: SystemModel) => void;
  onCustomize: (model: SystemModel) => void;
  onEditWorkspace: (row: WorkspaceTemplateRow) => void;
  onDeleteWorkspace: (row: WorkspaceTemplateRow) => void;
};

const ModelliListsBody: FC<ListsBodyProps> = ({
  waTemplates,
  smsTemplates,
  connectorsLoading,
  smsLoading,
  waTemplatesLoading,
  waTemplatesError,
  onRetryWa,
  statusById,
  statusSkeleton,
  onPreviewSystem,
  onCustomize,
  onEditWorkspace,
  onDeleteWorkspace,
}) => {
  const { openSlideOver } = useSlideOverContext();
  const { templates: emailTemplates, isError: emailError, isLoading: emailLoading } = useEmailTemplates();

  const allRows = useMemo(
    () =>
      buildWorkspaceTemplateRows({
        waTemplates,
        smsTemplates,
        emailTemplates: emailError ? [] : emailTemplates,
      }),
    [emailError, emailTemplates, smsTemplates, waTemplates]
  );

  const { customsByTopic, myTemplates } = useMemo(() => partitionWorkspaceTemplates(allRows), [allRows]);

  const openCustomPreview = (row: WorkspaceTemplateRow) =>
    openSlideOver({
      renderContent: ModelloPreviewSlideOver,
      renderContentProps: { model: { channel: row.channel, name: row.name, formData: row.formData } },
      showButtonInsideSlideOver: true,
    });

  const settling = connectorsLoading || emailLoading || Boolean(smsLoading) || Boolean(waTemplatesLoading);

  return (
    <>
      <ModelliTabContent
        statusById={statusById}
        statusSkeleton={statusSkeleton}
        customsByTopic={customsByTopic}
        onPreviewSystem={onPreviewSystem}
        onPreviewCustom={openCustomPreview}
        onCustomize={onCustomize}
        onEditCustom={onEditWorkspace}
        onDeleteCustom={onDeleteWorkspace}
      />
      <MyModelsList
        rows={myTemplates}
        settling={settling}
        waTemplatesError={waTemplatesError}
        onRetryWa={onRetryWa}
        onPreview={openCustomPreview}
        onEdit={onEditWorkspace}
        onDelete={onDeleteWorkspace}
      />
    </>
  );
};

type ModelliWorkspaceListsProps = {
  waTemplates: WhatsappTemplate[];
  connectorsLoading: boolean;
  waTemplatesLoading?: boolean;
  waTemplatesError?: boolean;
  onRetryWa?: () => void;
  statusById: Map<string, SystemModelStatus>;
  statusSkeleton: boolean;
  onPreviewSystem: (model: SystemModel) => void;
  onCustomize: (model: SystemModel) => void;
  onEditWorkspace: (row: WorkspaceTemplateRow) => void;
  onDeleteWorkspace: (row: WorkspaceTemplateRow) => void;
};

const ListsWithSms: FC<ModelliWorkspaceListsProps> = (props) => {
  const sms = useSmsWorkspaceTemplates();
  return (
    <ModelliListsBody
      {...props}
      smsTemplates={sms.isError ? [] : sms.templates}
      smsLoading={sms.isLoading}
    />
  );
};

/** Topic groups (system + prefixed customs) and My templates (unprefixed), from one workspace fetch. */
export const ModelliWorkspaceLists: FC<ModelliWorkspaceListsProps> = (props) => {
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const { data: customConnectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );
  const smsConnector = customConnectors?.find((connector) => connector.name === SMS_CONNECTOR_NAME);
  const settlingConnectors = props.connectorsLoading || isConnectorsLoading;

  const shared = { ...props, connectorsLoading: settlingConnectors };

  if (smsConnector?.isAuthenticated && smsConnector.id) {
    return (
      <SmsWorkspaceProvider connectorId={smsConnector.id}>
        <ListsWithSms {...shared} />
      </SmsWorkspaceProvider>
    );
  }
  return <ModelliListsBody {...shared} smsTemplates={[]} />;
};
